import { z } from 'zod';
import { idSchema, isoDateSchema } from '../primitives/branded.js';
import { positivePriceSchema } from '../primitives/numeric.js';
import { toPatchSchema } from '../primitives/patch.js';
import { bookingStatusSchema, stopLossBookingSchema } from './booking.js';
import { instrumentSchema } from './instrument.js';
import { stopLossEvaluationSchema, stopLossPriceSchema } from './stop-loss.js';
import { timestampSchema } from '../primitives/timestamp.js';
/**
 * A position is one row of the user's watchlist: an instrument plus the
 * stop-loss configuration that belongs to it. Holdings (quantity, entry price)
 * are optional so the same list can hold pure watchlist entries.
 */
/**
 * Die Kennung, unter der eine Position nach außen erscheint.
 *
 * Steht in Adressen und in den Links der täglichen Auswertung. Zufällig, damit
 * eine durchgesickerte Adresse weder das Wertpapier noch die Zahl der Positionen
 * verrät — die laufende `id` bleibt innen.
 *
 * `nullable`, weil Zeilen aus der Zeit vor dieser Spalte keine haben können. Die
 * Oberfläche fällt dann auf den ersten Eintrag zurück, statt einen Link zu
 * bauen, der nirgendwohin führt.
 */
const PUBLIC_ID_MUSTER = /^[A-Za-z0-9_-]{16,24}$/;
export const publicIdSchema = z.string().regex(PUBLIC_ID_MUSTER).nullable();
/**
 * Sieht die Zeichenkette wie eine Kennung aus?
 *
 * In `shared`, weil beide Seiten dieselbe Frage stellen: Der Server prüft, bevor
 * er die Datenbank bemüht, die Oberfläche, bevor sie einen Pfad deutet. Zwei
 * Muster an zwei Stellen liefen auseinander, sobald eines sich ändert.
 */
export const isPublicId = (value) => PUBLIC_ID_MUSTER.test(value);
export const positionSchema = z.object({
    id: idSchema,
    publicId: publicIdSchema,
    instrumentId: idSchema,
    quantity: z.number().finite().nonnegative().nullable(),
    entryPrice: positivePriceSchema.nullable(),
    entryDate: isoDateSchema.nullable(),
    notes: z.string().max(2000).nullable(),
    /** Der festgelegte Stop-Kurs; wird über die Chart-Linie gesetzt. */
    stopLossPrice: stopLossPriceSchema,
    /** Sort order inside the portfolio view, dense and user controlled. */
    sortOrder: z.number().int().nonnegative(),
    isActive: z.boolean(),
    /** Die beim Broker hinterlegte Order, sofern eingebucht. */
    booking: stopLossBookingSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const createPositionSchema = z.object({
    /** Either reference an existing instrument by id … */
    instrumentId: idSchema.optional(),
    /** … or let the API resolve/create it from a symbol. Exactly one is required. */
    symbol: z.string().trim().min(1).max(24).optional(),
    quantity: z.number().finite().nonnegative().nullish(),
    entryPrice: positivePriceSchema.nullish(),
    entryDate: isoDateSchema.nullish(),
    notes: z.string().max(2000).nullish(),
    /** Optional: Der Stop wird üblicherweise erst nach der Analyse gesetzt. */
    stopLossPrice: positivePriceSchema.nullish(),
    isActive: z.boolean().default(true),
});
/* Ohne Vorgabewerte: Sonst käme bei jeder Änderung `isActive: true` mit und
 * reaktivierte eine deaktivierte Position stillschweigend. */
export const updatePositionSchema = toPatchSchema(createPositionSchema.omit({ instrumentId: true, symbol: true }));
/** Explicit ordering payload so drag & drop in the UI persists in one request. */
export const reorderPositionsSchema = z.object({
    orderedIds: z.array(idSchema).min(1),
});
/**
 * Read model for the portfolio table: the position, its instrument, the latest
 * close and the evaluated stop level. Assembled once in the API so the web app
 * never has to join these by hand.
 */
/**
 * Read model for the portfolio card.
 *
 * Beträge sind konsequent benannt: Felder auf `…PerShare` beziehen sich auf eine
 * einzelne Aktie, alle übrigen Geldbeträge auf die **gesamte Position**, sind also
 * bereits mit der Stückzahl multipliziert. Ohne diese Trennung liest man leicht
 * einen Kursunterschied als Depotwirkung — bei 364 Stück ein Faktor, der über
 * Handeln oder Abwarten entscheidet.
 *
 * Alle positionsbezogenen Beträge sind `null`, solange keine Stückzahl hinterlegt
 * ist: Ein reiner Beobachtungswert hat keine Depotwirkung.
 */
export const positionViewSchema = positionSchema.extend({
    instrument: instrumentSchema,
    latestClose: z.number().finite().positive().nullable(),
    latestDate: isoDateSchema.nullable(),
    previousClose: z.number().finite().positive().nullable(),
    /**
     * Die jüngsten Schlusskurse, ältester zuerst — die Linie auf der Karte.
     *
     * Bewusst nur die Schlusskurse und nicht ganze Kerzen: Die Karte zeichnet eine
     * Linie, kein Kerzendiagramm. Wer Hoch, Tief und Eröffnung braucht, öffnet den
     * Chart, der sie ohnehin gesondert lädt.
     *
     * Leer, solange für den Wert noch keine Kurse vorliegen.
     */
    recentCloses: z.array(z.number().finite().positive()),
    /** Kursveränderung zum Vortag, je Aktie. */
    changePerShare: z.number().finite().nullable(),
    /** Dieselbe Veränderung in Prozent. */
    changePercent: z.number().finite().nullable(),
    /** Was der Tag für die Position ausmacht: `changePerShare × Stückzahl`. */
    changeValue: z.number().finite().nullable(),
    /** `Stückzahl × letzter Kurs`. */
    marketValue: z.number().finite().nullable(),
    /** `Stückzahl × Einstandskurs`. */
    costBasis: z.number().finite().nullable(),
    /** Buchgewinn oder -verlust der Position gegenüber dem Einstand. */
    unrealisedValue: z.number().finite().nullable(),
    /** Derselbe Wert relativ zum Einstand, in Prozent. */
    unrealisedPercent: z.number().finite().nullable(),
    stopLossEvaluation: stopLossEvaluationSchema,
    /** `Stückzahl × Stop-Kurs` — was die Position beim Auslösen noch wert wäre. */
    stopLossValue: z.number().finite().nullable(),
    /**
     * Was beim Auslösen des Stops gegenüber dem Einstand herauskäme.
     *
     * Die Zahl, auf die es beim Setzen eines Stops ankommt: Ein Stop über dem
     * Einstand sichert einen Gewinn, einer darunter begrenzt den Verlust.
     */
    stopLossResult: z.number().finite().nullable(),
    /** Was vom heutigen Wert abgegeben würde, falls der Stop greift. */
    riskValue: z.number().finite().nullable(),
    /** Die beim Broker hinterlegte Stop-Order, sofern eingebucht. */
    booking: stopLossBookingSchema.nullable(),
    /** Abgeleiteter Handlungsbedarf; siehe `deriveBookingStatus`. */
    bookingStatus: bookingStatusSchema,
});
//# sourceMappingURL=position.js.map